// Celebratory modal that fires the first time a Beginner Mode user
// completes skill #25 (the first Application-tier skill). Reframes the
// transition from "passive learner" to "active builder" by introducing
// agents — and routes them into /agents with a starter goal pre-filled.
//
// One-shot: as soon as the user dismisses or claims, we flag
// `agentUnlockShown` so we never re-celebrate the same milestone.
import { useNavigate } from "react-router-dom";
import { Bot, Sparkles, X, ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useBeginnerMode } from "@/hooks/useBeginnerMode";

const STARTER_GOAL =
  "Find 5 recent articles about a topic I care about, summarize each in 2 sentences, and email me the result.";

const AgentUnlockModal = () => {
  const navigate = useNavigate();
  const { justUnlockedAgent, dismissAgentUnlock } = useBeginnerMode();

  const handleBuild = () => {
    dismissAgentUnlock();
    const params = new URLSearchParams({
      goal: STARTER_GOAL,
      tools: "web_search,web_fetch",
    });
    navigate(`/agents/builder?${params.toString()}`);
  };

  return (
    <Dialog
      open={justUnlockedAgent}
      onOpenChange={(open) => {
        if (!open) dismissAgentUnlock();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-3 w-14 h-14 rounded-2xl bg-primary/15 grid place-items-center">
            <Bot className="w-7 h-7 text-primary" />
          </div>
          <DialogTitle className="text-center font-mono text-xl">
            <Sparkles className="inline w-4 h-4 text-primary mr-1" />
            You just leveled up.
          </DialogTitle>
          <DialogDescription className="text-center font-mono text-xs pt-2">
            You've finished the Awareness tier — 25 skills. You're not a
            consumer anymore. Time to make AI work <em>for you</em>.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 my-2">
          <p className="text-xs font-mono font-bold text-foreground mb-1">
            Build your first agent
          </p>
          <p className="text-[11px] font-mono text-muted-foreground leading-relaxed">
            An agent is an AI that uses tools (search, browse, code) to
            complete a goal — without you babysitting each step. We'll
            pre-fill a simple goal so you can ship one in 60 seconds.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleBuild}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-mono font-bold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Bot className="w-4 h-4" />
            Build my first agent
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={dismissAgentUnlock}
            className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-mono text-muted-foreground hover:text-foreground"
          >
            <X className="w-3 h-3" />
            Maybe later — keep learning
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AgentUnlockModal;
